/**
 * Component overrides that make the whole app behave on a phone, applied to BOTH
 * themes (admin + client) so no shell is left behind.
 *
 * These are deliberately global rather than per-page: the same four mistakes
 * repeat across ~40 dialogs, 11 tab bars and 10 tables, and fixing them at the
 * theme is both smaller and harder to regress than 60 individual edits.
 */

import type { Components, Theme } from '@mui/material/styles';

export const mobileComponents: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: {
      html: {
        // iOS inflates text in landscape unless told not to, which silently
        // breaks any layout tuned in portrait.
        WebkitTextSizeAdjust: '100%',
      },
      body: {
        // Long unbroken strings — emails, registry codes, Storage URLs — are the
        // most common cause of text running off the edge of a phone screen.
        // Breaks only when a word genuinely cannot fit.
        overflowWrap: 'break-word',
      },
      // Media must never be able to widen its container.
      'img, svg, video, canvas': {
        maxWidth: '100%',
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      // A `fullScreen` dialog (e.g. the arrival-guide editor on a phone) is
      // exempt: the margin and `calc(100% - 16px)` below would otherwise win
      // over MUI's own full-screen paper and leave an 8 px frame at 390 px.
      paper: ({ theme, ownerState }) =>
        ownerState?.fullScreen
          ? {}
          : {
              [theme.breakpoints.down('sm')]: {
                // MUI's default 32 px margin plus `maxWidth: 'sm'` leaves a cramped,
                // horizontally-scrolling dialog on a 375 px screen. Near-fullscreen
                // instead, while keeping the rounded card look.
                margin: 8,
                width: 'calc(100% - 16px)',
                maxWidth: 'calc(100% - 16px)',
                maxHeight: 'calc(100% - 16px)',
              },
            },
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        [theme.breakpoints.down('sm')]: {
          paddingLeft: 16,
          paddingRight: 16,
        },
      }),
    },
  },

  MuiTabs: {
    // Tab bars overflow the moment a translation runs long (Russian and Finnish
    // both do). Scrollable by default; the six call sites that already say
    // `variant="scrollable"` are unaffected, since explicit props win.
    defaultProps: {
      variant: 'scrollable',
      scrollButtons: 'auto',
      allowScrollButtonsMobile: true,
    },
  },

  MuiTableContainer: {
    styleOverrides: {
      root: {
        // Belt and braces: a table wider than the phone scrolls inside its own
        // container rather than dragging the page sideways.
        overflowX: 'auto',
        maxWidth: '100%',
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        [theme.breakpoints.down('sm')]: {
          paddingLeft: 8,
          paddingRight: 8,
        },
      }),
    },
  },
};

type AnyRecord = Record<string, unknown>;

function isPlainObject(v: unknown): v is AnyRecord {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Merge two `styleOverrides` maps slot by slot.
 *
 * A shallow spread is NOT enough: `clientTheme` already defines
 * `MuiCssBaseline.styleOverrides.body` (background colour), which would silently
 * drop the `overflowWrap` set here — exactly the kind of half-applied fix this
 * module exists to prevent. Function slots (MUI's callback form) can't be merged,
 * so the theme's own value wins there.
 */
function mergeSlots(base: unknown, own: unknown): unknown {
  if (!isPlainObject(base)) return own;
  if (!isPlainObject(own)) return base;
  const out: AnyRecord = { ...base };
  for (const [slot, ownValue] of Object.entries(own)) {
    const baseValue = out[slot];
    out[slot] =
      isPlainObject(baseValue) && isPlainObject(ownValue)
        ? { ...baseValue, ...ownValue }
        : ownValue;
  }
  return out;
}

/**
 * Merge two component-override maps: `own` wins on genuine conflicts,
 * everything else is combined (defaultProps per prop, styleOverrides per slot).
 * Used to layer foundation → portal theme (foundation.ts) as well as below.
 */
export function mergeComponents(
  base: Components<Theme>,
  own: Components<Theme> = {},
): Components<Theme> {
  const out: AnyRecord = { ...(base as AnyRecord) };
  for (const [component, ownCfg] of Object.entries(own as AnyRecord)) {
    const baseCfg = out[component];
    if (!isPlainObject(baseCfg) || !isPlainObject(ownCfg)) {
      out[component] = ownCfg;
      continue;
    }
    out[component] = {
      ...baseCfg,
      ...ownCfg,
      defaultProps: {
        ...(baseCfg.defaultProps as AnyRecord),
        ...(ownCfg.defaultProps as AnyRecord),
      },
      styleOverrides: mergeSlots(baseCfg.styleOverrides, ownCfg.styleOverrides),
    };
  }
  return out as Components<Theme>;
}

/**
 * Merge the shared mobile overrides with a theme's own component overrides.
 * The theme's values win on genuine conflicts; everything else is combined.
 */
export function withMobileComponents(own: Components<Theme> = {}): Components<Theme> {
  return mergeComponents(mobileComponents, own);
}
